import React from "react";
import PropTypes from "prop-types";
import { Badge, Button, ChevronLeft, ChevronRight, XClose } from "@opendate-app/setlist";
import { SetDot } from "./TicketSetTabs";
import TicketSetOverlaps from "./TicketSetOverlaps";
import PublishedLock from "./PublishedLock";
import {
  adjustPrice,
  adjustmentFor,
  colorOf,
  describeAdjustment,
  formatEntryTime,
  formatMoney,
  longDate,
  typesInSet,
} from "../ticketSets";

const PAGE = 3;
const WEEKDAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const monthLabel = (month, withYear) =>
  new Date(`${month}-01T12:00:00`).toLocaleDateString("en-US", { month: "long", ...(withYear && { year: "numeric" }) });

const monthCells = (month) => {
  const [year, monthNumber] = month.split("-").map(Number);
  const lead = new Date(year, monthNumber - 1, 1).getDay();
  const days = new Date(year, monthNumber, 0).getDate();
  return [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, index) => `${month}-${String(index + 1).padStart(2, "0")}`),
  ];
};

const DateCoverageCalendar = ({
  sets,
  activeSet,
  cover,
  ticketTypes,
  decisions,
  onToggleDate,
  onJumpToSet,
  onDecide,
  onClearDecision,
  locked,
}) => {
  const [selected, setSelected] = React.useState(null);
  const [start, setStart] = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);
  const picking = !locked && activeSet.appliesTo === "dates";
  const byKey = Object.fromEntries(sets.map((set) => [set.key, set]));
  const nameOf = (key) => (byKey[key] || {}).name;

  const months = [...new Set(cover.scheduled.map((date) => date.slice(0, 7)))];
  const multiYear = new Set(months.map((month) => month.slice(0, 4))).size > 1;
  const inMonth = (month) => cover.scheduled.filter((date) => date.startsWith(month));
  const hasIssue = (month) => inMonth(month).some((date) => cover.dates[date].uncovered || cover.dates[date].undecided);
  const paged = months.length > PAGE && !showAll;
  const fallbackStart = (() => {
    const issue = months.findIndex(hasIssue);
    if (issue >= 0) return issue;
    return Math.max(0, months.findIndex((month) => inMonth(month).some((date) => cover.dates[date].keys.includes(activeSet.key))));
  })();
  const maxStart = Math.max(0, months.length - PAGE);
  const first = Math.min(Math.max(0, start == null ? fallbackStart : start), maxStart);
  const visible = paged ? months.slice(first, first + PAGE) : months;
  const detail = !picking && selected && cover.dates[selected];

  const clickDate = (date) => {
    if (picking) onToggleDate(date);
    else setSelected(selected === date ? null : date);
  };

  return (
    <section className="series-step-panel series-set-coverage" data-testid="date-coverage">
      <header className="series-step-panel-header">
        <h3 className="series-step-panel-title">Date Coverage</h3>
      </header>
      <div className="series-step-panel-body">
        <p className="series-edit-hint">
          {picking ? `Click a date to add or remove it from ${activeSet.name}.` : "Click a date to see which ticket set each entry time uses."}
        </p>

        <div className="series-set-legend">
          {sets.map((set) => (
            <span key={set.key} className="series-set-legend-item">
              <SetDot color={colorOf(sets, set.key)} />
              {set.name}
              <span className="series-set-legend-count">{cover.dateCounts[set.key] || 0}</span>
            </span>
          ))}
          {cover.uncoveredDates.length > 0 && (
            <span className="series-set-legend-item">
              <span className="series-set-dot series-set-uncovered" aria-hidden="true" />
              Not covered
              <span className="series-set-legend-count">{cover.uncoveredDates.length}</span>
            </span>
          )}
          {cover.undecidedDates.length > 0 && (
            <span className="series-set-legend-item series-set-legend-warning">
              Needs a decision
              <span className="series-set-legend-count">{cover.undecidedDates.length}</span>
            </span>
          )}
        </div>

        <PublishedLock locked={Boolean(locked)}>
          <TicketSetOverlaps
            sets={sets}
            overlaps={cover.overlaps}
            decisions={decisions}
            allTimes={cover.times}
            onDecide={onDecide}
            onClear={onClearDecision}
          />
        </PublishedLock>

        {months.length > PAGE && (
          <div className="series-set-months-nav">
            {paged && (
              <Button variant="tertiary" size="sm" icon={ChevronLeft} aria-label="Earlier months" disabled={first === 0} onClick={() => setStart(Math.max(0, first - 1))} />
            )}
            {paged && (
              <span className="series-edit-hint">
                {monthLabel(visible[0], multiYear)} {"–"} {monthLabel(visible[visible.length - 1], multiYear)}
              </span>
            )}
            {paged && (
              <Button variant="tertiary" size="sm" icon={ChevronRight} aria-label="Later months" disabled={first >= maxStart} onClick={() => setStart(Math.min(maxStart, first + 1))} />
            )}
            <Button variant="link-gray" size="sm" onClick={() => setShowAll((current) => !current)}>
              {showAll ? "Show 3 months" : "Show all months"}
            </Button>
          </div>
        )}

        <div className="series-set-months">
          {visible.map((month) => (
            <div key={month} className="series-set-month">
              <div className="series-set-month-name">{monthLabel(month, multiYear)}</div>
              <div className="series-set-month-grid">
                {WEEKDAY_HEADERS.map((header) => (
                  <span key={header} className="series-set-weekday-header">
                    {header}
                  </span>
                ))}
                {monthCells(month).map((date, index) => {
                  if (!date) return <span key={`blank-${index}`} />;
                  const day = Number(date.slice(8));
                  const covered = cover.dates[date];
                  if (!covered) {
                    return (
                      <span key={date} className="series-set-day unscheduled">
                        {day}
                      </span>
                    );
                  }
                  const extras = covered.keys.filter((key) => key !== covered.key);
                  const tip = covered.keys.length
                    ? `${covered.keys.map(nameOf).join(" + ")}${covered.uncovered ? " · some entry times not covered" : ""}${covered.undecided ? " · overlap needs a decision" : ""}`
                    : covered.undecided
                      ? "Overlap needs a decision"
                      : "No ticket set covers this date";
                  return (
                    <button
                      key={date}
                      type="button"
                      title={tip}
                      aria-label={`${longDate(date)}: ${tip}`}
                      aria-pressed={picking ? (activeSet.dates || []).includes(date) : selected === date}
                      data-date={date}
                      className={[
                        "series-set-day",
                        covered.key ? `series-set-color-${colorOf(sets, covered.key)}` : "series-set-uncovered",
                        covered.undecided ? "undecided" : "",
                        covered.keys.includes(activeSet.key) ? "current" : "",
                        !picking && selected === date ? "selected" : "",
                      ]
                        .join(" ")
                        .trim()}
                      onClick={() => clickDate(date)}
                    >
                      {day}
                      {(extras.length > 0 || (covered.uncovered && covered.key)) && (
                        <span className="series-set-day-dots">
                          {extras.slice(0, 2).map((key) => (
                            <SetDot key={key} color={colorOf(sets, key)} />
                          ))}
                          {covered.uncovered && covered.key && <span className="series-set-dot series-set-uncovered" />}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {detail && (
          <div className="series-set-day-detail" data-testid="date-coverage-detail">
            <div className="series-set-day-detail-header">
              <strong>{longDate(selected)}</strong>
              <Button variant="tertiary" size="sm" icon={XClose} aria-label="Close" onClick={() => setSelected(null)} />
            </div>
            <ul className="series-review-list">
              {detail.slots.map((slot) => {
                const set = slot.key && byKey[slot.key];
                const adjustment = set ? adjustmentFor(set, slot.time) : null;
                const paid = set ? typesInSet(ticketTypes, sets, set.key).filter((type) => Number(type.price) > 0) : [];
                return (
                  <li key={slot.time} className="series-review-list-row series-set-day-slot">
                    <span className="series-set-day-slot-time">{formatEntryTime(slot.time)}</span>
                    {set ? (
                      <button
                        type="button"
                        className="series-set-badge-button"
                        aria-label={`Go to ${set.name}`}
                        title={`Go to ${set.name}`}
                        onClick={() => onJumpToSet(set.key)}
                      >
                        <Badge color={colorOf(sets, set.key)} size="sm">
                          {set.name}
                        </Badge>
                      </button>
                    ) : (
                      slot.undecided.length === 0 && (
                        <Badge color="error" size="sm">
                          Not covered
                        </Badge>
                      )
                    )}
                    {set && set.ageRestriction && <Badge size="sm">{set.ageRestriction}</Badge>}
                    {adjustment && <Badge size="sm">{`${adjustment.label || "Price adjustment"} ${describeAdjustment(adjustment)}`}</Badge>}
                    {slot.undecided.length > 0 && (
                      <Badge color="warning" size="sm">
                        Needs a decision
                      </Badge>
                    )}
                    <span className="series-review-list-meta">
                      {paid
                        .slice(0, 2)
                        .map((type) => `${type.name} ${formatMoney(adjustPrice(type.price, adjustment))}`)
                        .join(" · ")}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

DateCoverageCalendar.propTypes = {
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeSet: PropTypes.object.isRequired,
  cover: PropTypes.object.isRequired,
  ticketTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  decisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onToggleDate: PropTypes.func.isRequired,
  onJumpToSet: PropTypes.func.isRequired,
  onDecide: PropTypes.func.isRequired,
  onClearDecision: PropTypes.func.isRequired,
  locked: PropTypes.bool,
};

export default DateCoverageCalendar;
